// Shared shadcn Combobox for search-and-pick fields (replaces MUI Autocomplete).
// Options are objects. Pass `filter={null}` when the options already come from
// a server search, so the list shows them as they are. `showClear` shows a clear
// button once an option is picked (or, with `multiple`, once there are chips); `onClear` instead shows one whenever the
// typed text is not empty. Leave out `value` and `inputValue` to let the field
// keep the picked option and the typed text itself.
import React, { useEffect, useId, useRef, useState } from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { Search, XIcon } from "lucide-react";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from "../../shadcn/components/ui/combobox";
import { Button } from "../../shadcn/components/ui/button";
import { Field, FieldError, FieldLabel } from "../../shadcn/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../../shadcn/components/ui/input-group";
import { Spinner } from "../../shadcn/components/ui/spinner";
import { useTranslation } from "../../context/Internationalization";
import { cn } from "../../shadcn/lib/utils";

const defaultLabel = (option) => option?.label ?? "";

const SearchCombobox = ({
  label,
  ariaLabel,
  placeholder,
  options = [],
  value,
  onValueChange,
  inputValue,
  onInputValueChange,
  getOptionLabel = defaultLabel,
  getOptionKey,
  isOptionEqualToValue,
  filter,
  multiple = false,
  disabled = false,
  loading = false,
  emptyText,
  error,
  showSearchIcon = false,
  showClear = false,
  onClear,
  className,
}) => {
  const { t } = useTranslation();
  const id = useId();
  // Without `value` / `inputValue` from the caller, Base UI keeps them itself.
  // Only a field with `onClear` (a clear button for typed text) keeps them
  // here, so the button can see and empty the text.
  const valueControlled = value !== undefined;
  const textControlled = inputValue !== undefined;
  const ownsValue = !valueControlled && Boolean(onClear);
  const ownsText = !textControlled && Boolean(onClear) && !multiple;
  const [ownValue, setOwnValue] = useState(multiple ? [] : null);
  const selected = valueControlled ? value : ownValue;
  const [ownText, setOwnText] = useState(() =>
    ownsText && selected ? getOptionLabel(selected) : ""
  );
  // A selection changed from outside shows its label in the box.
  const shownSelection = useRef(selected);
  useEffect(() => {
    if (!ownsText || shownSelection.current === selected) return;
    shownSelection.current = selected;
    setOwnText(selected ? getOptionLabel(selected) : "");
  }, [ownsText, selected, getOptionLabel]);
  const text = textControlled ? inputValue : ownText;
  const changeValue = (next, details) => {
    if (ownsValue) setOwnValue(next);
    if (ownsText) shownSelection.current = next;
    onValueChange?.(next, details);
  };
  const changeText = (next, details) => {
    if (ownsText) setOwnText(next);
    onInputValueChange?.(next, details);
  };
  const errorId = `${id}-error`;
  const anchor = useComboboxAnchor();
  const optionKey = getOptionKey || getOptionLabel;
  const inputProps = {
    id,
    placeholder,
    "aria-label": label ? undefined : ariaLabel || placeholder,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? errorId : undefined,
  };

  return (
    <Field
      data-invalid={error ? true : undefined}
      className={cn("w-full gap-1.5", className)}
    >
      {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
      <Combobox
        items={options}
        multiple={multiple}
        value={valueControlled || ownsValue ? selected : undefined}
        onValueChange={changeValue}
        inputValue={textControlled || ownsText ? text : undefined}
        onInputValueChange={changeText}
        itemToStringLabel={getOptionLabel}
        isItemEqualToValue={isOptionEqualToValue}
        filter={filter}
        disabled={disabled}
      >
        {multiple ? (
          // Even padding round the chips; the typed text lines up with chip text.
          <ComboboxChips
            ref={anchor}
            className="has-data-[slot=combobox-chip]:px-2 has-data-[slot=combobox-chip]:py-2"
          >
            <ComboboxValue>
              {(selected) => (
                <>
                  {selected.map((option) => (
                    // A long name is cut with "…" so the chip fits its box
                    // and its remove button stays visible (MUI chips did
                    // the same).
                    <ComboboxChip
                      key={optionKey(option)}
                      // White with a border so chips stand out on the grey box.
                      className="min-w-0 max-w-full border border-border bg-background dark:bg-background"
                    >
                      <span
                        className="min-w-0 truncate"
                        title={getOptionLabel(option)}
                      >
                        {getOptionLabel(option)}
                      </span>
                    </ComboboxChip>
                  ))}
                  <ComboboxChipsInput
                    {...inputProps}
                    disabled={disabled}
                    className="h-6 px-2"
                  />
                  {showClear ? (
                    <ComboboxPrimitive.Clear
                      aria-label={t?.("combobox_clear")}
                      render={<Button variant="ghost" size="icon-xs" />}
                      disabled={disabled}
                    >
                      <XIcon className="pointer-events-none" />
                    </ComboboxPrimitive.Clear>
                  ) : null}
                </>
              )}
            </ComboboxValue>
          </ComboboxChips>
        ) : (
          // Built like the registry ComboboxInput, but its clear and open
          // buttons get names for screen readers.
          <InputGroup className="w-full">
            {showSearchIcon ? (
              <InputGroupAddon aria-hidden="true">
                <Search />
              </InputGroupAddon>
            ) : null}
            <ComboboxPrimitive.Input
              render={<InputGroupInput disabled={disabled} />}
              {...inputProps}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                variant="ghost"
                render={<ComboboxTrigger />}
                aria-label={t?.("combobox_open")}
                className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
                disabled={disabled}
              />
              {showClear ? (
                <ComboboxPrimitive.Clear
                  data-slot="combobox-clear"
                  aria-label={t?.("combobox_clear")}
                  render={<InputGroupButton variant="ghost" size="icon-xs" />}
                  disabled={disabled}
                >
                  <XIcon className="pointer-events-none" />
                </ComboboxPrimitive.Clear>
              ) : null}
              {onClear && text ? (
                <InputGroupButton
                  variant="ghost"
                  size="icon-xs"
                  data-slot="combobox-clear"
                  aria-label={t?.("combobox_clear")}
                  disabled={disabled}
                  onClick={() => {
                    if (ownsText) {
                      setOwnText("");
                      shownSelection.current = null;
                      onValueChange?.(null);
                    }
                    if (ownsValue) setOwnValue(null);
                    onClear();
                  }}
                >
                  <XIcon className="pointer-events-none" />
                </InputGroupButton>
              ) : null}
            </InputGroupAddon>
          </InputGroup>
        )}
        <ComboboxContent
          anchor={multiple ? anchor : undefined}
          // MUI showed no list when there was nothing to pick; Base UI would
          // draw an empty box.
          className={cn(!loading && !emptyText && "data-empty:hidden")}
        >
          {loading ? (
            <div className="flex justify-center py-2">
              <Spinner aria-label={t?.("loading")} />
            </div>
          ) : emptyText ? (
            <ComboboxEmpty>{emptyText}</ComboboxEmpty>
          ) : null}
          <ComboboxList>
            {(option) => (
              <ComboboxItem key={optionKey(option)} value={option}>
                <span className="min-w-0 break-words">
                  {getOptionLabel(option)}
                </span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </Field>
  );
};

export default SearchCombobox;
